import { AvlItem, useGetAvlItemsQuery } from '@/services/approvedVendorList';
import { useCreateConstructionAdderMutation } from '@/services/pricing';
import { Box, Button, FormikTextInput, Modal, toast } from '@palmetto/palmetto-components';
import { Field, FieldArray, Form, Formik } from 'formik';
import { SyntheticEvent, useCallback } from 'react';
import * as yup from 'yup';

import { AdderType, VendorEquipmentTypes, adderMap, ProgramType } from 'types';

const batteryPriceCapSchema = yup.object({
  name: yup.string().required('Option Name is required'),
  batteryPriceCaps: yup.mixed().test({
    test: function (value: any, { createError }) {
      return Object.entries(value ?? {}).every(([batteryId, priceCap]) => {
        if (priceCap === '' || priceCap === null || priceCap === undefined) {
          return true; // blank means "no cap for this battery" — valid, not an error.
        }
        const priceCapNumber = Number(priceCap);
        if (isNaN(priceCapNumber) || priceCapNumber < 0) {
          return createError({
            path: `batteryPriceCaps.${batteryId}`,
            message: 'Price Cap must be a positive number',
          });
        }
        return true;
      });
    },
  }),
});

interface BatteryPriceCapValues {
  name: string;
  batteryPriceCaps: Record<string, number>;
}

export const CreateBatteryPriceCapModal = ({
  isModalOpen,
  closeModal,
  adderType,
  programType,
}: {
  isModalOpen: boolean;
  closeModal: (event?: SyntheticEvent) => void;
  adderType?: AdderType;
  programType: ProgramType;
}) => {
  // Palmetto-admin-only pricing page, no account/org context available; matches original scoping.
  const { data: avlStorage } = useGetAvlItemsQuery({
    equipmentType: VendorEquipmentTypes.storage,
    programType,
  });
  const eligibleAvlStorage = avlStorage?.data?.filter((storage: AvlItem) =>
    adderType === AdderType.arbitrageBatteryPriceCap ? storage.arbitrage : storage.backup,
  );
  const [createConstructionAdder] = useCreateConstructionAdderMutation();

  const getDefaultValues = useCallback(() => {
    return {
      name: '',
      batteryPriceCaps: (eligibleAvlStorage ?? []).reduce(
        (acc: Record<string, number>, storage: AvlItem) => {
          // @ts-expect-error default to empty string for input values
          acc[storage.id] = storage.maxPrice ?? '';
          return acc;
        },
        {} as Record<string, number>,
      ),
    };
  }, [eligibleAvlStorage]);

  const handleSubmit = useCallback(
    async (values: BatteryPriceCapValues) => {
      const key =
        adderType === AdderType.arbitrageBatteryPriceCap ? 'arbitrageBatteryPriceCaps' : 'backupBatteryPriceCaps';
      try {
        await createConstructionAdder({
          data: {
            adderType: adderType,
            name: values.name,
            [key]: Object.entries(values.batteryPriceCaps ?? {}).map(([id, value]) => ({
              batteryId: id,
              maxPrice: value,
            })),
          },
          programType: programType,
        }).unwrap();
        toast.success('Adder created successfully');
        closeModal();
      } catch (e) {
        console.error(e);
        toast.error('Error creating adder');
      }
    },
    [adderType, closeModal, createConstructionAdder],
  );

  return (
    <Modal ariaLabelledBy="createConstructionAdder" isOpen={isModalOpen} onDismiss={closeModal} maxWidth="500px">
      <Modal.Header
        id="createConstructionAdder"
        title={`Add ${adderMap[programType][adderType as AdderType]} Modifier`}
        onDismiss={closeModal}
      />
      <Formik
        initialValues={getDefaultValues()}
        validationSchema={batteryPriceCapSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ resetForm, isSubmitting }) => {
          return (
            <Form>
              <Modal.Body overflow={'auto'} maxHeight={'70vh'} background="secondary">
                <Box direction="column" childGap="md">
                  <Field component={FormikTextInput} label="Option Name" name="name" id="name" isRequired />
                  <Box childGap="lg">
                    <Box fontWeight="medium" color="body-primary">
                      {adderType === AdderType.backupBatteryPriceCap ? 'Backup' : 'Arbitrage'} Battery Price Caps
                    </Box>
                    <Box gap="lg">
                      <FieldArray
                        name="batteryPriceCaps"
                        render={() =>
                          (eligibleAvlStorage ?? []).map((battery: AvlItem) => (
                            <Field
                              prefix="$"
                              type="number"
                              component={FormikTextInput}
                              label={battery.manufacturer + ' - ' + battery.modelNumber}
                              name={`batteryPriceCaps.${battery.id}`}
                              id={`batteryPriceCaps.${battery.id}`}
                              key={battery.id}
                              isRequired
                            />
                          ))
                        }
                      />
                    </Box>
                  </Box>
                </Box>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  as="button"
                  onClick={() => {
                    resetForm();
                    closeModal();
                  }}
                  variant="secondary"
                  tone="neutral"
                  size="md"
                  isLoading={isSubmitting}
                >
                  Cancel
                </Button>
                <Button size="md" type="submit" variant="primary" isLoading={isSubmitting}>
                  Add Option
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
